import React, { useEffect, useState } from 'react';
import CustomLoading from '../../components/customLoading/CustomLoading';
import Table from '../../components/tables/Table';
import Breadcrumbs from '../../components/BreadCrumbs';
import Pagination from '../../components/tables/pagination/Pagination';
import { toast } from 'react-toastify';
import { formatDate } from '../../utils/FormatDateTime';
import { getSatelliteApprovedAnalytics, getSatelliteApprovedFarms, patchSatelliteFarm } from '../../services/satelliteFarm';
import SearchBar from '../../components/searchBar/SearchBar';
import Dropdown from '../../components/inputFields/DropDown';
import { BiRefresh } from 'react-icons/bi';
import ShowModal from '../../utils/ShowModal';
import SatelliteDetailsPop from './SatelliteDetailsPop';
import StyledHeadingText from '../../components/Fonts/StyledHeadingText';
import Card from '../../components/cards/Card';
import { useSearchParams } from 'react-router-dom';
import {
    FaUsers, FaUserTie, FaStore, FaTractor, FaSatellite,
    FaBoxes, FaSeedling, FaMobileAlt, FaChartBar,
    FaIndustry, FaTags, FaClipboardList, FaMapMarkedAlt,
    FaClock, FaSearchDollar, FaBox
} from 'react-icons/fa';
import Tab from '../../components/tabs/Tab';
import { confirmDialog } from '../../utils/confirmDialog';
import { getUserId } from '../../helpers/local_storage_helpers';

function SatelliteApprovedFarms() {
    const [searchParams, setSearchParams] = useSearchParams();

    const [cardData, setCardData] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [subTab, setSubTab] = useState("in_process");
    const [filterSubsEnded, setFilterSubsEnded] = useState(false);

    const [modalIsOpen, setIsOpen] = useState(false);
    const [openingItem, setOpeningItem] = useState({});

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const headers = [
        { label: "S.No.", key: "index" },
        { label: "Name", key: "user_name" },
        { label: "Amount", key: "payment" },
        { label: "Subs. End", key: "subscription_end_date" },
        { label: "(Area) Farm", key: "farm" },
        { label: "User Phone", key: "user_phone_number" },
        { label: "Farm St.", key: "status" },
        { label: "Last Updated", key: "updated_at" },
        { label: "Notes", key: "sales_team_notes" },
        { label: "Type", key: "is_free" },
        { label: "Sales Team App.", key: "sales_team_approval" },
    ];

    const pageSize = 50;

    // Helper function to update URL parameters
    const updateURLParams = (updates) => {
        const newParams = new URLSearchParams(searchParams);

        Object.entries(updates).forEach(([key, value]) => {
            if (value === null || value === undefined || value === "" ||
                (typeof value === 'boolean' && !value)) {
                newParams.delete(key);
            } else {
                newParams.set(key, value);
            }
        });

        setSearchParams(newParams, { replace: true });
    };

    // Helper function to get URL parameters
    const getURLParams = () => {
        const params = {};

        // Get search text
        params.search = searchParams.get('search') || '';

        // Get sub tab
        params.subTab = searchParams.get('subTab') || 'in_process';

        // Get subscription ended filter
        params.subsEnded = searchParams.get('subsEnded') === 'true';

        // Get current page
        params.page = parseInt(searchParams.get('page')) || 1;

        return params;
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        updateURLParams({ page });

        // Fetch data with the new page
        const urlParams = getURLParams();
        fetchData(urlParams, page);
    };

    const handleSearch = (query) => {
        setSearchText(query);
        updateURLParams({ search: query, page: 1 });
        const urlParams = getURLParams();
        fetchData({ ...urlParams, search: query }, 1);
    };

    const handleSubsEnded = () => {
        const newValue = !filterSubsEnded;
        setFilterSubsEnded(newValue);
        updateURLParams({ subsEnded: newValue, page: 1 });
        const urlParams = getURLParams();
        fetchData({ ...urlParams, subsEnded: newValue }, 1);
    };

    const handleSubTabChange = (value) => {
        setSubTab(value);
        updateURLParams({ subTab: value, page: 1 });
        const urlParams = getURLParams();
        fetchData({ ...urlParams, subTab: value }, 1);
    };

    const resetFilters = () => {
        setSearchText("");
        setSubTab("in_process");
        setFilterSubsEnded(false);
        setCurrentPage(1);

        // Clear all URL parameters
        setSearchParams({}, { replace: true });
        fetchData({ search: "", subTab: "in_process", subsEnded: false }, 1);
    };

    const openModal = (item) => {
        setOpeningItem(item);
        setIsOpen(true);
    };

    const closeModal = () => {
        setIsOpen(false);
        setOpeningItem({});
    };

    const fetchData = async (params, page) => {
        setLoading(true);
        try {
            const urlParams = params || getURLParams();
            const currentPageNum = page || urlParams.page;

            let query = `search=${urlParams.search}&subs_ended=${urlParams.subsEnded}&page=${currentPageNum}`;

            if (urlParams.subTab === "in_process") {
                query += `&farm_status=in_process`;
            } else if (urlParams.subTab === "free") {
                query += `&farm_status=correct&is_free=true`;
            } else if (urlParams.subTab === "paid") {
                query += `&farm_status=correct&is_free=false`;
            }

            let res = await getSatelliteApprovedFarms(query);
            if (res.status === 200) {
                const data = res.data;
                setData(data);
            } else {
                toast.error("Unable to fetch Data");
            }
            setLoading(false);
        } catch (error) {
            console.error("Error:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAnalytics = async () => {
        try {
            let res = await getSatelliteApprovedAnalytics();
            if (res.status === 200) {
                const data = res.data;
                setCardData(data);
            } else {
                toast.error("Unable to fetch Data");
            }
            setLoading(false);
        } catch (error) {
            console.error("Error:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleEditNote = async (item) => {
        const result = await confirmDialog(
            "Update Sales Team Notes",
            "Edit Note",
            {
                showInput: true,
                inputPlaceholder: "Enter notes...",
                confirmText: "Update",
                inputValue: item.sales_team_notes || ""
            }
        );

        if (result !== false) {
            setLoading(true);
            try {
                const response = await patchSatelliteFarm(item.id, {
                    sales_team_notes: result.notes,
                    sales_team: getUserId()
                });
                if (response.status === 200) {
                    toast.success("Note updated successfully");
                    const urlParams = getURLParams();
                    fetchData(urlParams);
                } else {
                    toast.error("Unable to update note");
                }
            } catch (error) {
                console.error("Error updating note:", error);
                toast.error("An error occurred while updating the note");
            } finally {
                setLoading(false);
            }
        }
    };

    const checkFarmApproval = async (id, newStatus, no_of_approved_farms = 0) => {
        let notes = "";
        let freeTrial = true;
        if (newStatus === "approved") {
            const result = await confirmDialog(
                "Please provide any notes for this approval.",
                "Approve Farm",
                {
                    showInput: true,
                    inputPlaceholder: "Enter approval notes...",
                    showToggle: true,
                    toggleTitle: "Are you providing free trial?",
                    toggleValue: true,
                    confirmText: "Approve",
                    type: "success"
                }
            );

            if (result === false) {
                // User cancelled or closed the modal
                const urlParams = getURLParams();
                fetchData(urlParams);
                return;
            }
            notes = result.notes;
            freeTrial = result.toggle;
        } else if (newStatus === "rejected") {
            const result = await confirmDialog(
                "Please provide a reason for rejection.",
                "Reject Farm",
                {
                    showInput: true,
                    inputPlaceholder: "Enter rejection reason...",
                    confirmText: "Reject",
                    type: "danger"
                }
            );

            if (result === false) {
                const urlParams = getURLParams();
                fetchData(urlParams);
                return;
            }
            notes = result.notes;
        }

        if (no_of_approved_farms > 0 && newStatus === "approved") {
            const confirm = await confirmDialog(
                "More than 1 farm is approved for this user. Are you sure you want to approve this farm?",
                "Confirm Farm Approval",
                {
                    type: "warning",
                    confirmText: "Approve",
                    cancelText: "Cancel"
                }
            );

            if (confirm) {
                patchDropdownData(id, newStatus, notes, freeTrial);
            } else {
                const urlParams = getURLParams();
                fetchData(urlParams);
            }
        } else {
            patchDropdownData(id, newStatus, notes, freeTrial);
        }
    };

    const patchDropdownData = async (id, newStatus, notes = "", freeTrial = false) => {
        setLoading(true);
        try {
            const dataToSend = {
                sales_team_approval: newStatus,
                sales_team_notes: notes,
                sales_team: getUserId(),
            };
            if (newStatus === "approved") {
                dataToSend.is_free = freeTrial;
            }
            let res = await patchSatelliteFarm(id, dataToSend);
            if (res.status === 200) {
                const urlParams = getURLParams();
                fetchData(urlParams);
            } else {
                toast.error("Unable to Update status");
            }
            setLoading(false);
        } catch (error) {
            console.error("Error:", error);
        } finally {
            setLoading(false);
        }
    };

    // Initialize state from URL parameters and fetch data once on mount
    useEffect(() => {
        const urlParams = getURLParams();

        setSearchText(urlParams.search);
        setSubTab(urlParams.subTab);
        setFilterSubsEnded(urlParams.subsEnded);
        setCurrentPage(urlParams.page);

        // Fetch data and analytics on mount
        fetchData(urlParams);
        fetchAnalytics();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Empty dependency array - only run on mount

    const getStatIcon = (title) => {
        const iconMap = {
            // Existing mappings
            "Total Users": FaUsers,
            "Total Staff Users": FaUserTie,
            "Total Dukaan Users": FaStore,
            "Total Farms": FaTractor,
            "Total Satellite Records": FaSatellite,
            "Total Commodities": FaBoxes,
            "Total Practice Commodities": FaSeedling,
            "Total FPO": FaUsers,
            "Total User Devices": FaMobileAlt,
            "Total Shops": FaStore,
            "Total Manufacturers": FaIndustry,
            "Total Categories": FaTags,
            "Total Products": FaBoxes,
            "Total Subscription": FaClipboardList,
            "Total Plans": FaBox,
            "Pending Subscription": FaClock,
            "Pending Review": FaSearchDollar,
            "Total Area(In Hect.)": FaMapMarkedAlt
        };

        const IconComponent = iconMap[title] || FaChartBar;
        return IconComponent;
    };

    return (<>
        <Breadcrumbs items={[
            { name: 'Home', link: '/dashboard' },
            { name: 'Satellite Approved Farms' },
        ]} />

        <div className="py-3">
            <StyledHeadingText text={"Summary"} />
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 mt-2">
                {cardData.map((card, index) => (
                    <Card title={card.title} count={card.value} key={index} icon={getStatIcon(card.title)} />
                ))}
            </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-4">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-1 w-full lg:w-auto">
                    <div className="w-full sm:w-72">
                        <SearchBar
                            placeholder="Search by Number / Name"
                            onSearch={handleSearch}
                            value={searchText}
                        />
                    </div>

                    <div className="flex items-center gap-3 bg-gray-50 px-4 py-2 rounded-lg border border-gray-200">
                        <div
                            onClick={handleSubsEnded}
                            className={`relative inline-flex items-center h-5 rounded-full w-10 transition-all duration-200 cursor-pointer ${filterSubsEnded
                                ? 'bg-primary shadow-sm'
                                : 'bg-gray-300 hover:bg-gray-400'
                                }`}
                        >
                            <span
                                className={`inline-block w-4 h-4 transform transition-transform duration-200 rounded-full bg-white shadow ${filterSubsEnded
                                    ? 'translate-x-5'
                                    : 'translate-x-0.5'
                                    }`}
                            />
                        </div>
                        <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
                            Subscription Ended
                        </span>
                    </div>
                </div>

                {/* Active Filters Summary & Reset */}
                <div className="flex items-center gap-3">
                    {(searchText.length !== 0 || subTab !== "in_process" || filterSubsEnded) && (
                        <>
                            <div className="flex items-center gap-2 flex-wrap">
                                {searchText && (
                                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                        Search: {searchText.substring(0, 15)}{searchText.length > 15 ? '...' : ''}
                                    </span>
                                )}
                                {filterSubsEnded && (
                                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                        Subs. Ended
                                    </span>
                                )}
                            </div>
                            <button
                                onClick={resetFilters}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-primary hover:bg-primary/5 rounded-lg transition-colors"
                                title="Reset all filters"
                            >
                                <BiRefresh size={18} />
                                <span className="hidden sm:inline">Reset</span>
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
        <div className="my-2 space-y-2">
            <Tab tabsList={[
                { name: "In Progress", id: "in_process" },
                { name: "Free", id: "free" },
                { name: "Paid", id: "paid" },
            ]} onClick={handleSubTabChange} activeTab={subTab} />
        </div>
        {
            loading ?
                <div className="flex w-full flex-row justify-center my-2">
                    <CustomLoading size={100} />
                </div>
                :
                <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                    <Table
                        headers={headers}
                        rows={data?.results?.map((item, index) => (
                            {
                                index: (
                                    <div
                                        onClick={() => openModal(item)}
                                        className={`cursor-pointer w-8 h-8 flex items-center justify-center ${item.has_satellite_data ? "bg-green-500" : "bg-red-500"} text-white font-semibold text-sm rounded-full shadow-sm hover:shadow-md transition-shadow`}
                                    >
                                        {index + 1}
                                    </div>
                                ),
                                user_name: (
                                    <p
                                        onClick={() => openModal(item)}
                                        className='max-w-28 cursor-pointer truncate font-medium text-gray-800 hover:text-primary transition-colors'
                                        title={item.user_name}
                                    >
                                        {item.user_name}
                                    </p>
                                ),
                                farm: (
                                    <div onClick={() => openModal(item)} className='max-w-44 cursor-pointer group'>
                                        <p className='truncate font-medium text-gray-700 group-hover:text-primary transition-colors' title={item.farm.name}>
                                            {item.farm.name}
                                        </p>
                                        <p className='text-xs text-gray-500'>
                                            {item.farm.size} {item.farm.area_unit}
                                        </p>
                                    </div>
                                ),
                                user_address: (
                                    <span className='flex cursor-pointer flex-row gap-1 items-center'>
                                        <p className="max-w-40 truncate text-gray-600 text-sm" title={item.user_address}>
                                            {item.user_address}
                                        </p>
                                    </span>
                                ),
                                user_phone_number: (
                                    <p
                                        className='cursor-pointer text-gray-700 font-mono text-sm hover:text-primary transition-colors'
                                        onClick={() => openModal(item)}
                                    >
                                        {item.user_phone_number}
                                    </p>
                                ),
                                payment: (
                                    <span
                                        onClick={() => openModal(item)}
                                        className={`inline-flex items-center px-2.5 py-1 text-sm font-semibold rounded-lg cursor-pointer ${item.paid_amount > 0 ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'
                                            }`}
                                    >
                                        ₹{item.paid_amount}
                                    </span>
                                ),
                                status: (
                                    <div
                                        onClick={() => openModal(item)}
                                        className={`inline-flex items-center justify-center capitalize rounded-lg px-3 py-1.5 text-xs font-semibold cursor-pointer transition-all hover:shadow-sm ${item.farm_status === "correct"
                                            ? "bg-green-100 text-green-700 border border-green-200"
                                            : item.farm_status === "in_process"
                                                ? "bg-yellow-100 text-yellow-700 border border-yellow-200"
                                                : "bg-red-100 text-red-700 border border-red-200"
                                            }`}
                                    >
                                        {item.farm_status === "in_process" ? "In Progress" : item.farm_status}
                                    </div>
                                ),
                                sales_team_approval: (
                                    <div className="min-w-32">
                                        <Dropdown
                                            label=''
                                            onChange={(value) => { checkFarmApproval(item.id, value, item.no_of_approved_farms); }}
                                            value={item.sales_team_approval}
                                            placeholder={item.sales_team_approval || "Choose"}
                                            options={[
                                                { label: "Pending", value: "pending" },
                                                { label: "Approved", value: "approved" },
                                                { label: "Rejected", value: "rejected" },
                                            ]}
                                        />
                                    </div>
                                ),
                                subscription_start_date: (
                                    <p className='cursor-pointer text-sm text-gray-600' onClick={() => openModal(item)}>
                                        {formatDate(item.subscription_start_date)}
                                    </p>
                                ),
                                subscription_end_date: (
                                    <p
                                        onClick={() => openModal(item)}
                                        className={`cursor-pointer text-sm font-medium ${(new Date(item.subscription_end_date) < new Date() && item.farm_status === "correct")
                                            ? 'text-red-600'
                                            : 'text-gray-700'
                                            }`}
                                    >
                                        {formatDate(item.subscription_end_date)}
                                    </p>
                                ),
                                is_free: (
                                    <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold ${item.is_free
                                        ? 'bg-blue-50 text-blue-700 border border-blue-200'
                                        : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                        }`}>
                                        {item.is_free ? 'Free' : 'Paid'}
                                    </span>
                                ),
                                sales_team_notes: (
                                    <p
                                        className="max-w-44 truncate cursor-pointer text-sm text-gray-600 hover:text-primary transition-colors"
                                        title={`Click to edit: ${item.sales_team_notes || "No notes"}`}
                                        onClick={() => handleEditNote(item)}
                                    >
                                        {item.sales_team_notes || <span className="italic text-gray-400">Add note...</span>}
                                    </p>
                                ),
                                updated_at: (
                                    <p className='cursor-pointer text-sm text-gray-500' onClick={() => openModal(item)}>
                                        {formatDate(item.updated_at)}
                                    </p>
                                ),
                                rowBgColor: (new Date(item.subscription_end_date) < new Date()) && item.farm_status === "correct"
                                    ? 'bg-red-50 hover:bg-red-100'
                                    : 'bg-white hover:bg-gray-50',
                            }))}
                    />
                </div >}
        {/* Table End */}
        {/* Pagination Begin */}
        {
            data.count !== 0 ?
                <Pagination
                    currentPage={currentPage}
                    pageSize={pageSize}
                    totalEntries={data?.count}
                    onPageChange={handlePageChange}
                    data={data}
                    setData={setData}
                />
                : <></>
        }
        <ShowModal
            contentLabel={"Farm Details alert"}
            isOpen={modalIsOpen}
            modalWidth="80%"
            isSidebar={true}
            onRequestClose={closeModal}
            componentToPass={<SatelliteDetailsPop item={openingItem} onAnyChange={() => {
                const urlParams = getURLParams();
                fetchData(urlParams);
            }} />}
        />
    </>);
}

export default SatelliteApprovedFarms;
